// Design System EntityCard Component
// Unified card component for displaying entity information

import React, { useState } from 'react';
import {
  Typography,
  Box,
  IconButton,
  LinearProgress,
  Chip,
  useTheme,
  alpha,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Card, 
  CardContent,
  styled
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BarChart as AnalyticsIcon,
  Task as TaskIcon,
  Group as TeamIcon,
  AttachMoney as BudgetIcon,
  Style as StyleIcon,
  AutoAwesome as MagicIcon,
  Person as DefaultAvatarIcon
} from '@mui/icons-material';
import SecureAvatar from '../../../components/ui/SecureAvatar';

// Import design tokens
import { spacingTokens, colorUtils } from '../../tokens';

// Styled card component using design system tokens
const StyledCard = styled(Card)(({ 
  theme, 
  magical = false,
  interactive = false,
  topBorder = null,
  leftBorder = null,
  variant = 'default',
  brandColors = []
}) => {
  const mode = theme.palette.mode;
  
  // Base styles using design tokens
  const baseStyles = {
    position: 'relative',
    borderRadius: spacingTokens.component.lg,
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    backgroundColor: theme.palette.background.paper,
    overflow: 'hidden',
    border: `1px solid ${alpha(theme.palette.divider, mode === 'dark' ? 0.12 : 0.08)}`,
    minHeight: 320,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  // Variant styles
  const variantStyles = {
    default: {},
    elevated: {
      boxShadow: theme.shadows[2],
    },
    outlined: {
      border: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
    },
  };

  // Border styles
  const borderStyles = {
    ...(topBorder && {
      borderTop: `4px solid ${topBorder}`,
    }),
    ...(leftBorder && {
      borderLeft: `4px solid ${leftBorder}`,
    }),
  };

  // Brand color gradient styles
  const brandGradientStyles = brandColors.length > 0 ? {
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '120px',
      background: brandColors.length === 1
        ? `linear-gradient(180deg, ${alpha(brandColors[0].hex, 0.08)} 0%, transparent 100%)`
        : `linear-gradient(135deg, ${alpha(brandColors[0].hex, 0.06)} 0%, ${alpha(brandColors[1]?.hex || brandColors[0].hex, 0.06)} 100%)`,
      pointerEvents: 'none',
      zIndex: 0,
    },
  } : {};

  // Interactive styles
  const interactiveStyles = interactive ? {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
      '& .entity-card-actions': {
        opacity: 1,
      },
    },
  } : {};

  // Magical styles
  const magicalStyles = magical ? {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, 
        transparent 0%,
        ${alpha(theme.palette.primary.main, 0.05)} 50%,
        transparent 100%)`,
      animation: 'magicalShimmer 3s infinite ease-in-out',
      zIndex: 0,
    },
    '@keyframes magicalShimmer': {
      '0%, 100%': {
        opacity: 0.3,
      },
      '50%': {
        opacity: 0.7,
      },
    },
    '& > *': {
      position: 'relative',
      zIndex: 1,
    },
  } : {};

  return {
    ...baseStyles,
    ...variantStyles[variant],
    ...borderStyles,
    ...brandGradientStyles,
    ...interactiveStyles,
    ...magicalStyles,
  };
});

/**
 * EntityCard - Unified card component for displaying entity information
 * 
 * Features:
 * - Support for multiple entity types (client, brand, campaign, project, etc.)
 * - Flexible content slots (avatar, title, subtitle, description, chips, stats, progress)
 * - Action buttons and menu
 * - Responsive design
 * - Magical effects support
 * - Consistent with design system
 */
const EntityCard = ({
  // Entity data
  entityType = 'default',
  data = {},
  
  // Content
  title,
  subtitle,
  description,
  avatar,
  status,
  chips = [],
  stats = [],
  progress,
  
  // Actions
  actions = [],
  primaryAction,
  secondaryAction,
  onView,
  onEdit,
  onDelete,
  onClick,
  
  // Styling
  variant = 'default',
  topBorder,
  leftBorder,
  magical = false,
  interactive = false,
  
  // Navigation
  linkTo,
  
  // Other props
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Process entity data based on type
  const processedData = React.useMemo(() => {
    // Debug log to see what data is being passed
    console.log(`EntityCard [${entityType}] data:`, data);
    
    const result = {
      title: title || data.name || data.title || '',
      subtitle: subtitle || '',
      description: description || data.description || '',
      avatar: avatar,
      status: status || data.status,
      chips: chips,
      stats: stats,
      progress: progress,
      actions: actions,
    };
    
    // Entity-specific processing
    switch (entityType) {
      case 'client':
        result.avatar = avatar || {
          src: data.logo?.url || data.logo?.key,
          fallback: <DefaultAvatarIcon />,
        };
        result.chips = chips.length > 0 ? chips : [
          data.region ? { label: data.region } : null,
          data.industry ? { label: data.industry } : null,
        ].filter(Boolean);
        result.stats = stats.length > 0 ? stats : [
          { label: 'Brands', value: data.brands?.length || 0 },
          { label: 'Campaigns', value: data.campaigns?.length || 0 },
          { label: 'Content', value: data.content?.length || 0 },
        ];
        break;
        
      case 'brand':
        result.avatar = avatar || {
          src: data.logo?.primary || data.logo,
          fallback: <DefaultAvatarIcon />,
        };
        result.chips = chips.length > 0 ? chips : [
          data.industry?.primary ? { label: data.industry.primary } : null,
        ].filter(Boolean);
        result.topBorder = topBorder || (data.colors?.[0]?.hex);
        // Add brand colors to the result for display
        result.brandColors = data.colors || [];
        break;
        
      case 'project':
        result.chips = chips.length > 0 ? chips : [
          data.client?.name ? { label: data.client.name } : null,
          data.priority ? { label: data.priority, color: data.priority === 'high' ? 'error' : 'default' } : null,
        ].filter(Boolean);
        result.progress = progress || {
          value: data.progress || 0,
          label: 'Progress',
          color: data.status === 'completed' ? 'success' : 'primary',
        };
        break;
        
      default:
        break;
    }
    
    return result;
  }, [entityType, data, title, subtitle, description, avatar, status, chips, stats, progress, actions, topBorder]);

  // Default actions based on callbacks
  const defaultActions = React.useMemo(() => {
    return [
      onView && {
        label: 'View',
        icon: <ViewIcon fontSize="small" />,
        onClick: onView,
      },
      onEdit && {
        label: 'Edit',
        icon: <EditIcon fontSize="small" />,
        onClick: onEdit,
      },
      onDelete && {
        label: 'Delete',
        icon: <DeleteIcon fontSize="small" />,
        onClick: onDelete,
        color: 'error',
      },
    ].filter(Boolean);
  }, [onView, onEdit, onDelete]);

  const effectiveActions = processedData.actions.length > 0 ? processedData.actions : defaultActions;

  // Handle menu
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (action) => {
    handleMenuClose();
    action.onClick?.();
  };

  const handleCardClick = (event) => {
    if (onClick) {
      onClick(event);
    }
  };

  // Render avatar
  const renderAvatar = () => {
    if (!processedData.avatar) return null;
    
    if (React.isValidElement(processedData.avatar)) {
      return processedData.avatar;
    }

    return (
      <SecureAvatar
        src={processedData.avatar.src}
        alt={processedData.avatar.alt || processedData.title}
        sx={{
          width: 48,
          height: 48,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
          border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        }}
      >
        {processedData.avatar.fallback || processedData.title?.charAt(0)}
      </SecureAvatar>
    );
  };

  // Render chips
  const renderChips = () => {
    if (!processedData.chips?.length) return null;

    // Filter out null/undefined chips
    const validChips = processedData.chips.filter(chip => chip != null);
    
    if (validChips.length === 0) return null;

    return (
      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 2 }}>
        {validChips.map((chip, index) => {
          if (React.isValidElement(chip)) {
            return chip;
          }

          // Ensure chip has required properties
          if (!chip || !chip.label) {
            console.warn('Invalid chip data:', chip);
            return null;
          }

          return (
            <Chip
              key={index}
              label={chip.label}
              size="small"
              color={chip.color || 'default'}
              variant={chip.variant || 'outlined'}
              sx={{
                fontSize: '0.75rem',
                height: 24,
              }}
            />
          );
        })}
      </Stack>
    );
  };

  // Render stats
  const renderStats = () => {
    if (!processedData.stats?.length) return null;

    return (
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {processedData.stats.map((stat, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.background.default, 0.5),
              borderRadius: spacingTokens.component.sm,
              py: 1,
              px: 0.5,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              minWidth: 0,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: 1.2,
                mb: 0.25,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                lineHeight: 1,
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  // Render brand colors
  const renderBrandColors = () => {
    if (entityType !== 'brand' || !processedData.brandColors?.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          Brand Colors
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {processedData.brandColors.slice(0, 5).map((color, index) => (
            <Box
              key={index}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                bgcolor: color.hex,
                border: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                boxShadow: theme.shadows[2],
                position: 'relative',
                cursor: 'pointer',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  zIndex: 1,
                },
                ...(color.isPrimary && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 12,
                    height: 12,
                    bgcolor: theme.palette.secondary.main,
                    borderRadius: '50%',
                    border: `1px solid ${theme.palette.background.paper}`,
                  },
                }),
              }}
              title={`${color.name} (${color.hex})${color.isPrimary ? ' - Primary' : ''}`}
            />
          ))}
          {processedData.brandColors.length > 5 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                ml: 0.5,
              }}
            >
              +{processedData.brandColors.length - 5} more
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };

  // Render progress
  const renderProgress = () => {
    if (!processedData.progress) return null;

    const { value, color = 'primary', label } = processedData.progress;

    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {label || 'Progress'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {value}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={value}
          color={color}
          sx={{
            height: 6,
            borderRadius: spacingTokens.component.xs,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            '& .MuiLinearProgress-bar': {
              borderRadius: spacingTokens.component.xs,
            },
          }}
        />
      </Box>
    );
  };

  // Render actions
  const renderActions = () => {
    if (!primaryAction && !secondaryAction && effectiveActions.length === 0) {
      return <Box sx={{ height: '100%' }} />;
    }

    return (
      <Box
        className="entity-card-actions"
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          opacity: interactive ? 0.7 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        {/* Primary Action */}
        {primaryAction && (
          <Button
            variant={primaryAction.variant || 'contained'}
            size="small"
            color={primaryAction.color || 'primary'}
            startIcon={primaryAction.icon}
            onClick={primaryAction.onClick}
            sx={{ mr: 1, flex: secondaryAction ? undefined : 1 }}
          >
            {primaryAction.label}
          </Button>
        )}

        {/* Secondary Action */}
        {secondaryAction && (
          <Button
            variant={secondaryAction.variant || 'outlined'}
            size="small"
            color={secondaryAction.color || 'primary'}
            startIcon={secondaryAction.icon}
            onClick={secondaryAction.onClick}
            sx={{ mr: 1, flex: 1 }}
          >
            {secondaryAction.label}
          </Button>
        )}

        {/* Actions Menu */}
        {effectiveActions.length > 0 && (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                ml: 'auto',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: spacingTokens.component.sm,
              }}
            >
              <MoreIcon fontSize="small" />
            </IconButton>

            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {effectiveActions.map((action, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleMenuItemClick(action)}
                  sx={{
                    color: action.color === 'error' ? 'error.main' : undefined,
                  }}
                >
                  {action.icon && (
                    <ListItemIcon
                      sx={{
                        color: action.color === 'error' ? 'error.main' : undefined,
                      }}
                    >
                      {action.icon}
                    </ListItemIcon>
                  )}
                  {action.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
    );
  };

  return (
    <StyledCard
      magical={magical}
      interactive={interactive || !!onClick || !!linkTo}
      topBorder={processedData.topBorder || topBorder}
      leftBorder={leftBorder}
      variant={variant}
      brandColors={entityType === 'brand' ? processedData.brandColors : []}
      onClick={handleCardClick}
      sx={sx}
      {...rest}
    >
      <CardContent sx={{ p: spacingTokens.layout.sm, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header Section - Fixed height */}
        <Box sx={{ minHeight: 60, mb: 2 }}>
          {/* Avatar */}
          {processedData.avatar && (
            <Box sx={{ mb: spacingTokens.component.sm }}>
              {renderAvatar()}
            </Box>
          )}

          {/* Title and Status */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Typography
              variant="h6"
              component={linkTo ? Link : 'h3'}
              to={linkTo}
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                lineHeight: 1.4,
                flexGrow: 1,
                minWidth: 0,
                textDecoration: 'none',
                color: 'text.primary',
                ...(linkTo && {
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline',
                  },
                }),
              }}
              noWrap
              title={processedData.title}
            >
              {processedData.title || '\u00A0'}
            </Typography>

            {processedData.status && (
              React.isValidElement(processedData.status) ? (
                processedData.status
              ) : (
                <Chip
                  label={processedData.status}
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )
            )}
          </Box>
        </Box>

        {/* Content Section - Flexible height with minimum */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 120 }}>
          {/* Subtitle */}
          <Typography 
            variant="body2" 
            color="text.secondary" 
            gutterBottom
            sx={{ minHeight: 20 }}
          >
            {processedData.subtitle || '\u00A0'}
          </Typography>

          {/* Description */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              lineHeight: 1.5,
              minHeight: 40,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {processedData.description || '\u00A0'}
          </Typography>

          {/* Chips */}
          <Box sx={{ minHeight: processedData.chips?.length > 0 ? 'auto' : 0, mb: processedData.chips?.length > 0 ? 2 : 0 }}>
            {renderChips()}
          </Box>

          {/* Stats */}
          <Box sx={{ minHeight: processedData.stats?.length > 0 ? 'auto' : 0, mb: processedData.stats?.length > 0 ? 2 : 0 }}>
            {renderStats()}
          </Box>

          {/* Brand Colors */}
          <Box sx={{ minHeight: processedData.brandColors?.length > 0 ? 'auto' : 0, mb: processedData.brandColors?.length > 0 ? 2 : 0 }}>
            {renderBrandColors()}
          </Box>

          {/* Progress */}
          <Box sx={{ minHeight: processedData.progress ? 'auto' : 0 }}>
            {renderProgress()}
          </Box>

          {/* Spacer to push actions to bottom */}
          <Box sx={{ flex: 1 }} />
        </Box>

        {/* Actions Section - Fixed at bottom */}
        <Box sx={{ minHeight: 48 }}>
          {renderActions()}
        </Box>

        {/* Magical indicator */}
        {magical && (
          <Box
            sx={{
              position: 'absolute',
              top: spacingTokens.component.md,
              right: spacingTokens.component.md,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              color: theme.palette.secondary.main,
            }}
          >
            <MagicIcon fontSize="small" />
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

// Create a simple BaseCard for compatibility
export const BaseCard = ({ children, ...props }) => {
  return (
    <EntityCard {...props}>
      {children}
    </EntityCard>
  );
};

export default EntityCard;